import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Printer, Pencil, Eye, Trash2 } from "lucide-react";
import api from "../api";
import { fmt } from "../utils";
import { useLang } from "../i18n";
import { toast } from "sonner";
import DocumentPreviewDialog from "../components/print/DocumentPreviewDialog";

export default function Purchases() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const { t } = useLang();
  const load = () => api.get("/purchases").then((r) => setRows(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, []);

  const removePurchase = async (bill) => {
    if (!window.confirm(`Delete purchase ${bill.bill_no}? This is allowed only while its stock is untouched.`)) return;
    try {
      await api.delete(`/purchases/${bill.id}`);
      toast.success(`Purchase ${bill.bill_no} deleted`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Purchase delete nahi ho paaya");
    }
  };

  return (
    <div data-testid="purchases-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("purchases")}</h1>
          <p className="sgp-page-subtitle">Company se kharida maal — save hote hi stock badh jayega.</p>
        </div>
        <Link to="/purchases/new" data-testid="new-purchase-btn"
          className="sgp-primary-btn">
          <Plus className="h-4 w-4" /> {t("newPurchase")}
        </Link>
      </div>
      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr>
              <th className="sgp-th">Bill No.</th><th className="sgp-th">Supplier Inv.</th><th className="sgp-th">Date</th>
              <th className="sgp-th">Company</th><th className="sgp-th text-right">Grand Total</th>
              <th className="sgp-th text-right">Settled</th><th className="sgp-th text-right">Due</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody data-testid="purchases-table">
            {rows.map((b) => (
              <tr key={b.id} data-testid={`purchase-row-${b.id}`} className="sgp-table-row">
                <td className="sgp-td">
                  <p className="font-mono text-xs font-semibold text-slate-900">{b.bill_no}</p>
                  {b.edited_at && <p className="mt-1 text-[10px] font-semibold text-amber-700">Edited · {new Date(b.edited_at).toLocaleString("en-IN")} · {b.edited_by}</p>}
                </td>
                <td className="sgp-td font-mono text-xs">{b.invoice_no || "-"}</td>
                <td className="sgp-td font-mono text-xs">{b.date}</td>
                <td className="sgp-td">{b.company_name}</td>
                <td className="sgp-td text-right font-mono">
                  {fmt(b.grand_total)}
                  {Number(b.returned_total || 0) > 0 && <p className="text-[10px] text-amber-700 mt-1">Return −{fmt(b.returned_total)}</p>}
                </td>
                <td className="sgp-td text-right font-mono text-emerald-700">
                  {fmt(b.settled_amount ?? b.paid_amount)}
                  {Number(b.tds_deducted_amount || 0) > 0 && <p className="text-[10px] text-slate-500 mt-1">incl. TDS {fmt(b.tds_deducted_amount)}</p>}
                </td>
                <td className="sgp-td text-right font-mono" >
                  <span className={Number(b.balance_due ?? (b.grand_total - b.paid_amount)) > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}>
                    {fmt(b.balance_due ?? (b.grand_total - b.paid_amount))}
                  </span>
                </td>
                <td className="sgp-td whitespace-nowrap">
                  <button data-testid={`preview-purchase-${b.id}`} type="button" onClick={() => setPreview({ kind: "purchase", id: b.id, label: `Purchase Bill ${b.bill_no}` })}
                    className="p-1.5 rounded hover:bg-blue-50 text-slate-500 hover:text-blue-700 inline-block" title="Preview"><Eye className="h-4 w-4" /></button>
                  <Link data-testid={`edit-purchase-${b.id}`} to={`/purchases/${b.id}/edit`}
                    className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700 inline-block" title="Edit with audit trail"><Pencil className="h-4 w-4" /></Link>
                  <Link data-testid={`print-purchase-${b.id}`} to={`/print/purchase/${b.id}`} target="_blank" rel="noreferrer"
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-teal-700 inline-block" title="Print / PDF"><Printer className="h-4 w-4" /></Link>
                  <button data-testid={`delete-purchase-${b.id}`} type="button" onClick={() => removePurchase(b)}
                    className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700 inline-block" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="sgp-td text-center py-10 text-slate-400">Abhi koi purchase bill nahi. Pehle company aur item master banao, fir bill banayo.</td></tr>}
          </tbody>
        </table>
      </div>
      <DocumentPreviewDialog open={!!preview} onClose={() => setPreview(null)} kind={preview?.kind} id={preview?.id} label={preview?.label} />
    </div>
  );
}
